import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { clearIcon } from '../shared/icons/clear';
import { searchIcon } from '../shared/icons/search';

@customElement('media-search')
export class Search extends LitElement {
  static styles = css`
    :host {
      font-family: Roboto, sans-serif;
      --border-color: #ccc;
      --primary-color: #ff9800;
    }

    .search {
      display: flex;
      align-items: center;
      border-radius: 12px;
      border: 1px solid var(--border-color);
      transition: all 0.2s ease-in-out;
      box-sizing: border-box;
      min-width: 356px;
      min-height: 42px;
      background-color: #fff;
      gap: 12px;
    }

    .search:hover,
    .search:has(.root:hover),
    .search:has(input:hover) {
      border-color: var(--primary-color);
      box-shadow: rgb(3 3 5 / 58%) 0px 20px 20px -20px;
      cursor: pointer;
    }

    .search:has(input:focus) {
      border-color: var(--primary-color);
    }

    .search .root {
      display: flex;
      flex: 1;
    }

    .search .icon {
      padding-left: 12px;
      height: 20px;
      width: 20px;
    }

    .search .icon svg {
      height: 20px;
      width: 20px;
    }

    .search .icon.clear {
      padding-right: 12px;
      transition: all 0.2s ease-in-out;
    }

    .search .root input {
      border: none;
      outline: none;
      background: transparent;
      display: flex;
      flex: 1;
      width: 100%;
      padding: 6px 0px;
    }
  `;

  @property({ type: String })
  placeholder = 'Search...';

  @property({ type: Function })
  onSearch?: (value: string) => void;

  @state()
  searchTerms = '';

  /**
   * Updates the search terms and dispatches a search event.
   */
  private updateSearchTerms(value: string) {
    this.onSearch?.(value);
    this.searchTerms = value;
    this.dispatchEvent(
      new CustomEvent('search', {
        detail: {
          value: this.searchTerms,
        },
      })
    );
  }

  /**
   * Clears the search terms
   */
  private clearSearch() {
    this.updateSearchTerms('');
  }

  /**
   * Handles input events and updates the search terms.
   */
  private handleInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.updateSearchTerms(target.value);
  }

  /**
   * Renders the clear icon
   */
  renderClear() {
    if (!this.searchTerms) {
      return;
    }

    return html`
      <div class="icon clear" @click="${this.clearSearch}">${clearIcon}</div>
    `;
  }

  render() {
    return html`
      <div class="search">
        <div class="icon">${searchIcon}</div>
        <div class="root">
          <input
            name="search"
            id="search"
            type="text"
            placeholder=${this.placeholder}
            .value=${this.searchTerms}
            @input="${this.handleInput.bind(this)}"
          />
        </div>

        ${this.renderClear()}
      </div>
    `;
  }
}
